import { AlertTriangle, Loader2 } from 'lucide-react';

import { Button } from './Button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './Dialog';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  /** Defaults suit the common "are you sure?" case; override for clearer verbs. */
  confirmLabel?: string;
  cancelLabel?: string;
  /** `destructive` reds the confirm button and shows the warning icon. */
  variant?: 'default' | 'destructive';
  /** Set while the confirmed action is in flight — disables both buttons and locks the dialog. */
  loading?: boolean;
  /** Rendered in place; the dialog stays open so the user can retry or back out. */
  error?: string | null;
  onConfirm: () => void;
}

/**
 * Confirmation prompt for actions worth a second look.
 *
 * Replaces `window.confirm`, which blocks the main thread and cannot be themed.
 * Deliberately presentational: the caller owns the async work and passes `loading`
 * and `error` back in, so there is no second copy of that state to keep in sync.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'default',
  loading = false,
  error = null,
  onConfirm,
}: Readonly<ConfirmDialogProps>) {
  // Closing mid-request would strand the action with nothing left to report back to.
  const blockDismiss = (event: Event) => {
    if (loading) event.preventDefault();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !loading && onOpenChange(next)}>
      <DialogContent
        hideClose={loading}
        className="max-w-md"
        onEscapeKeyDown={blockDismiss}
        onInteractOutside={blockDismiss}
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            {variant === 'destructive' && (
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-500/15">
                <AlertTriangle className="h-5 w-5 text-rose-400" />
              </div>
            )}
            <DialogTitle>{title}</DialogTitle>
          </div>
          <DialogDescription className="pt-3 text-slate-400">{description}</DialogDescription>
        </DialogHeader>

        {error && (
          <p className="mt-4 rounded-lg border border-rose-500/30 bg-rose-500/10 p-3 text-xs text-rose-300">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" disabled={loading} onClick={() => onOpenChange(false)}>
            {cancelLabel}
          </Button>
          <Button
            variant={variant === 'destructive' ? 'destructive' : 'default'}
            disabled={loading}
            onClick={onConfirm}
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {confirmLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
